import {
  MutationResult,
  OperationVariables,
  QueryResult,
} from '@apollo/client';
import { Async, F1, toAsyncError, toAsyncFetched } from './types';
import { GQLCollectionItem } from '../pages/collections/collections.domain';
import { useEffect, useMemo, useState } from 'react';
import { makeAsync } from './async';

export type GQLCollectionResult<
  T,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
> = QueryResult<GQLCollectionResultPayload<T>, any>;

export type GQLCollectionResultPayload<T> = {
  __typename?: 'Query' | undefined;
  collections: Array<GQLCollectionItem<T>>;
};

type MutationResultLight<TOut> = Pick<
  MutationResult<TOut>,
  'data' | 'loading'
> &
  Partial<Pick<MutationResult<TOut>, 'called'>> & {
    error?: Error | null | undefined;
  };

export const resultsToAsync = <
  TOut,
  TIn extends MutationResultLight<TOut> = MutationResultLight<TOut>,
>(
  queries: Array<TIn>,
  err?: Error | null,
): Async => {
  const mapped = queries.map((v) => resultToAsync(v, err));
  if (mapped.every((v) => v.type === 'fetched'))
    return { type: 'fetched', value: {} };
  return mapped.find((v) => v.type !== 'fetched') || { type: 'not-fetched' };
};

export const resultToAsync = <
  TOut,
  TIn extends MutationResultLight<TOut> = MutationResultLight<TOut>,
>(
  query: TIn,
  err?: Error | null,
): Async<TOut> => {
  if (err) return makeAsync.error(err.message);
  if (query.called === false) return makeAsync.notFetched();
  if (query.loading) return makeAsync.fetching();
  if (query.data) return makeAsync.fetched(query.data);
  if (query.error) return makeAsync.error(query.error.message);
  return makeAsync.notFetched();
};

export const useAsyncResult = <
  TOut,
  TIn extends MutationResultLight<TOut> = MutationResultLight<TOut>,
>(
  query: TIn,
  err?: Error | null,
): Async<TOut> =>
  useMemo(
    () => resultToAsync<TOut>(query, err),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query.called, query.data, query.loading, query.error, err],
  );

export const useMappedAsyncResult = <
  TOut,
  TOut2,
  TIn extends MutationResultLight<TOut> = MutationResultLight<TOut>,
>(
  mutation: TIn,
  mapper: F1<TOut, TOut2>,
): Async<TOut2> => {
  const state = useAsyncResult<TOut, TIn>(mutation);
  return mapAsync(state, mapper);
};

export const collectionToAsync = <
  TOut,
  TIn extends GQLCollectionResult<
    GQLCollectionItem<TOut>
  > = GQLCollectionResult<GQLCollectionItem<TOut>>,
>(
  query: TIn,
): Async<Array<TOut>> => {
  if (query.error) return makeAsync.error(query.error.message);
  if (query.loading) return makeAsync.fetching();
  if (query.data)
    return makeAsync.fetched(
      query.data['collections'].map(({ __typename, ...v }) => v as TOut),
    );
  return makeAsync.notFetched();
};

export const mapAsync = <T1, T2>(v: Async<T1>, mapper: F1<T1, T2>): Async<T2> =>
  v.type !== 'fetched' ? v : makeAsync.fetched(mapper(v.value));

export const mapAsyncCollection = <T1, T2>(
  v: Async<Array<T1>>,
  mapper: F1<T1, T2>,
): Async<Array<T2>> =>
  v.type !== 'fetched' ? v : makeAsync.fetched(v.value.map(mapper));

const toErrorMessage = (err: unknown, fallback: string): string => {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
};

export const useAsyncQuery = <
  TDataKey extends keyof TData,
  TData,
  TVars extends OperationVariables,
  TMapped,
>(
  query: QueryResult<TData, TVars>,
  key: TDataKey,
  mapper: F1<TData[TDataKey], TMapped>,
  defaultEmpty?: TMapped,
) => {
  const [data, setData] = useState<Async<TMapped>>({
    type: 'not-fetched',
  });

  const res = useAsyncResult<TData>(query);

  useEffect(() => {
    if (
      res.type === 'not-fetched' &&
      !query.called &&
      typeof defaultEmpty !== 'undefined'
    ) {
      setData(toAsyncFetched(defaultEmpty));
      return;
    }
    if (res.type !== 'fetched') setData(res);
    else {
      try {
        const value = mapper(res.value[key]);
        const d = toAsyncFetched(value);
        if (JSON.stringify(d) === JSON.stringify(data)) return;
        setData(d);
      } catch (err) {
        setData({
          type: 'fetch-error',
          value: toErrorMessage(err, 'Cannot map model'),
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [res.type]);

  const refetch = () => {
    setData({ type: 'fetching' });
    return query
      .refetch()
      .then((v) => {
        const mapped = mapper(v.data[key]);
        const next = toAsyncFetched(mapped);
        setData(next);
        return mapped;
      })
      .catch((err) => {
        const e = toAsyncError(toErrorMessage(err, 'Cannot map model'));
        setData(e);
        throw new Error(e.value);
      });
  };

  const softRefetch = () => {
    return query
      .refetch()
      .then((v) => {
        const mapped = mapper(v.data[key]);
        const next = toAsyncFetched(mapped);
        setData(next);
        return mapped;
      })
      .catch((err) => {
        const e = toAsyncError(toErrorMessage(err, 'Cannot map model'));
        setData(e);
        throw new Error(e.value);
      });
  };

  return { refetch, softRefetch, data };
};

export const useAsyncQueryRaw = <
  TData,
  TVars extends OperationVariables,
  TMapped = TData,
>({
  query,
  mapper,
  defaultEmpty,
}: {
  query: QueryResult<TData, TVars>;
  mapper: F1<TData, TMapped>;
  defaultEmpty?: TMapped;
}) => {
  const [data, setData] = useState<Async<TMapped>>({
    type: 'not-fetched',
  });

  const res = useAsyncResult<TData>(query);

  useEffect(() => {
    if (
      res.type === 'not-fetched' &&
      !query.called &&
      typeof defaultEmpty !== 'undefined'
    ) {
      setData(toAsyncFetched(defaultEmpty));
      return;
    }
    if (res.type !== 'fetched') setData(res);
    else {
      try {
        const value = mapper(res.value);
        const d = toAsyncFetched(value);
        if (JSON.stringify(d) === JSON.stringify(data)) return;
        setData(d);
      } catch (err) {
        setData({
          type: 'fetch-error',
          value: toErrorMessage(err, 'Cannot map model'),
        });
      }
    }
  }, [res]);

  const refetch = () => {
    setData({ type: 'fetching' });
    return query
      .refetch()
      .then((v) => {
        const mapped = mapper(v.data);
        const next = toAsyncFetched(mapped);
        setData(next);
        return mapped;
      })
      .catch((err) => {
        const e = toAsyncError(toErrorMessage(err, 'Cannot map model'));
        setData(e);
        throw new Error(e.value);
      });
  };

  const softRefetch = () => {
    return query
      .refetch()
      .then((v) => {
        const mapped = mapper(v.data);
        const next = toAsyncFetched(mapped);
        setData(next);
        return mapped;
      })
      .catch((err) => {
        const e = toAsyncError(toErrorMessage(err, 'Cannot map model'));
        setData(e);
        throw new Error(e.value);
      });
  };

  /**
   * Like softRefetch, but leaves the visible fetched data in place when the
   * refetch fails. Returns `{ ok: false, message }` instead of throwing so
   * callers can surface the failure (e.g., a toast in the ViewModel)
   * without collapsing the UI into an error state after a successful
   * mutation.
   */
  const silentRefetch = (): Promise<
    { ok: true } | { ok: false; message: string }
  > => {
    return query
      .refetch()
      .then((v) => {
        const mapped = mapper(v.data);
        setData(toAsyncFetched(mapped));
        return { ok: true as const };
      })
      .catch((err) => {
        const message = toErrorMessage(err, 'Cannot refresh');
        return { ok: false as const, message };
      });
  };

  return { refetch, softRefetch, silentRefetch, data };
};
